import React, { useState, useEffect } from 'react'

interface SimpleCaptchaProps {
  onVerify: (isValid: boolean) => void
}

interface CaptchaChallenge {
  question: string
  answer: number
}

const SimpleCaptcha: React.FC<SimpleCaptchaProps> = ({ onVerify }) => {
  const [challenge, setChallenge] = useState<CaptchaChallenge>({ question: '', answer: 0 })
  const [userAnswer, setUserAnswer] = useState('')
  const [error, setError] = useState(false)
  const [verified, setVerified] = useState(false)
  const [attempts, setAttempts] = useState(0)
  const [locked, setLocked] = useState(false)

  const generateChallenge = () => {
    // Generate random math problems
    const operations = [
      () => {
        const a = Math.floor(Math.random() * 10) + 1
        const b = Math.floor(Math.random() * 10) + 1
        return { question: `${a} + ${b}`, answer: a + b }
      },
      () => {
        const a = Math.floor(Math.random() * 10) + 10
        const b = Math.floor(Math.random() * 10) + 1
        return { question: `${a} - ${b}`, answer: a - b }
      },
      () => {
        const a = Math.floor(Math.random() * 5) + 1
        const b = Math.floor(Math.random() * 5) + 1
        return { question: `${a} × ${b}`, answer: a * b }
      },
      () => {
        // Simple word problems in Czech
        const items = [
          { name: 'jablka', singular: 'jablko' },
          { name: 'hrušky', singular: 'hruška' },
          { name: 'banány', singular: 'banán' },
          { name: 'pomeranče', singular: 'pomeranč' }
        ]
        const item = items[Math.floor(Math.random() * items.length)]
        const count = Math.floor(Math.random() * 9) + 1
        const action = Math.random() > 0.5
        
        if (action) {
          const add = Math.floor(Math.random() * 5) + 1
          return {
            question: `Máte ${count} ${count === 1 ? item.singular : item.name} a přidáte ${add}. Kolik jich máte celkem?`,
            answer: count + add
          }
        } else {
          const remove = Math.floor(Math.random() * Math.min(count, 5)) + 1
          return {
            question: `Máte ${count} ${count === 1 ? item.singular : item.name} a ${remove} odeberete. Kolik jich zbyde?`,
            answer: count - remove
          }
        }
      }
    ]

    const operation = operations[Math.floor(Math.random() * operations.length)]
    const newChallenge = operation()
    setChallenge(newChallenge)
    setUserAnswer('')
    setError(false)
    setVerified(false)
  }

  useEffect(() => {
    generateChallenge()
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (locked) {
      return
    }

    const answer = parseInt(userAnswer.trim())
    if (isNaN(answer)) {
      setError(true)
      return
    }

    if (answer === challenge.answer) {
      setVerified(true)
      setError(false)
      setAttempts(0)
      onVerify(true)
    } else {
      setError(true)
      onVerify(false)
      setAttempts(prev => prev + 1)
      
      // Lock after 5 attempts
      if (attempts >= 4) {
        setLocked(true)
        setTimeout(() => {
          setLocked(false)
          setAttempts(0)
          generateChallenge()
        }, 30000) // 30 second cooldown
      } else {
        // Generate new challenge after wrong answer
        setTimeout(() => {
          generateChallenge()
        }, 1500)
      }
    }
  }

  const handleRefresh = () => {
    if (!locked) {
      generateChallenge()
      onVerify(false)
      setError(false)
      setVerified(false)
    }
  }

  return (
    <div className="captcha-container">
      <div className="captcha-header">
        <span className="captcha-title">Ověření, že nejste robot</span>
        <button 
          type="button"
          className="captcha-refresh" 
          onClick={handleRefresh}
          title="Nová otázka"
          aria-label="Získat novou otázku"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
          </svg>
        </button>
      </div>
      
      <div className="captcha-question">
        {challenge.question} = ?
      </div>

      <form onSubmit={handleSubmit} className="captcha-form">
        <input
          type="text"
          value={userAnswer}
          onChange={(e) => {
            setUserAnswer(e.target.value)
            setError(false)
          }}
          placeholder="Vaše odpověď"
          className={`captcha-input ${error ? 'error' : ''} ${verified ? 'verified' : ''} ${locked ? 'locked' : ''}`}
          disabled={verified || locked}
          autoComplete="off"
          aria-label="Odpověď na ověřovací otázku"
        />
        
        {verified && (
          <div className="captcha-success">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
            </svg>
          </div>
        )}
      </form>

      {error && !verified && !locked && (
        <div className="captcha-error">
          Nesprávná odpověď, zkuste to znovu
        </div>
      )}

      {locked && (
        <div className="captcha-locked">
          Příliš mnoho neúspěšných pokusů. Zkuste to prosím za 30 sekund.
        </div>
      )}

      {attempts > 2 && !locked && !verified && (
        <div className="captcha-warning">
          Zbývají {5 - attempts} pokusy
        </div>
      )}
    </div>
  )
}

export default SimpleCaptcha